import { styled } from '@linaria/react';
import { Loader, Paper, Portal, ScrollArea, Text, TextInput } from '@mantine/core';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSimpleEpics } from '../../../../api/tasks-client/tasks-client.hooks';
import { useTranslation } from 'react-i18next';
import { useGlobalStore } from '../../../../store/global-store/global-store';
import { newCOLORS } from '../../../../styles/colors';
import { Icon } from '../../../../ui-library/icon/icon';
import { useWorkPeriodsData } from '../../../work-periods/hooks/work-periods.hooks';
import { getMentionTypeColor, getMentionTypeLabel, getMentionTypeLabelPlural } from './flora-mention.helpers';
import { CATEGORY_ICONS, CATEGORY_ORDER, type MentionItem, type MentionType } from './flora-mention.type';

interface BoardItem {
  id: string;
  name: string;
  projectName: string;
  sprintCount: number | null;
}

interface MentionPopoverProps {
  isOpen: boolean;
  items: MentionItem[];
  boards: BoardItem[];
  searchQuery: string;
  onSelect: (item: MentionItem) => void;
  onClose: () => void;
  anchorRef?: React.RefObject<HTMLElement | null>;
  dataTheme?: string;
}

// Categories view -> Items view for regular types
// Categories view -> Boards view -> Sprints view for sprints
// Categories view -> Epics view for epics (fetched on category select)
type ViewMode = 'categories' | 'items' | 'boards' | 'sprints' | 'epics';

export const MentionPopover = ({
  isOpen,
  items,
  boards,
  searchQuery,
  onSelect,
  onClose,
  anchorRef,
  dataTheme,
}: MentionPopoverProps) => {
  const i18n = useTranslation();
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [viewMode, setViewMode] = useState<ViewMode>('categories');
  const [selectedCategory, setSelectedCategory] = useState<MentionType | null>(null);
  const [selectedBoardId, setSelectedBoardId] = useState<string | null>(null);
  const [selectedBoardName, setSelectedBoardName] = useState<string | null>(null);
  const [epicSearchQuery, setEpicSearchQuery] = useState('');
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0, width: 0 });
  const listRef = useRef<HTMLDivElement>(null);
  const epicSearchRef = useRef<HTMLInputElement>(null);

  // Fetch epics when epic category is selected
  const portfolio = useGlobalStore((state) => state.portfolio);
  const epicEnabled = selectedCategory === 'epic';
  const { epics: epicData, query: epicsQuery } = useSimpleEpics({
    enabled: epicEnabled && !!portfolio?.id,
  } as Parameters<typeof useSimpleEpics>[0]);
  const isLoadingEpics = epicsQuery.isLoading && selectedCategory === 'epic';
  const isErrorEpics = epicsQuery.isError && selectedCategory === 'epic';

  // Fetch sprints for selected board
  const { data: sprintsData, isLoading: isLoadingSprints } = useWorkPeriodsData(selectedBoardId);

  // Filter boards to only show those with sprints
  const boardsWithSprints = useMemo(() => {
    return boards.filter((board) => board.sprintCount && board.sprintCount > 0);
  }, [boards]);

  // Convert sprints to MentionItems
  // For V1, filter to only sprints with overlap in the last 90 days
  const { sprintItems } = useMemo((): {
    sprintItems: MentionItem[];
  } => {
    if (!sprintsData || !selectedBoardId) return { sprintItems: [] };

    const items = sprintsData
      .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime())
      .map((sprint) => ({
        id: sprint.id,
        name: sprint.name,
        type: 'sprint' as MentionType,
        parentName: selectedBoardName || undefined,
        parentId: selectedBoardId,
      }));

    return { sprintItems: items };
  }, [sprintsData, selectedBoardId, selectedBoardName]);

  // Convert epics to MentionItems
  const epicItems = useMemo((): MentionItem[] => {
    if (!epicData) return [];
    return epicData.map((epic) => ({
      id: epic.id,
      name: epic.name ?? epic.title ?? epic.id ?? '',
      type: 'epic' as MentionType,
    }));
  }, [epicData]);

  // Filter epics by local search query
  const filteredEpicItems = useMemo((): MentionItem[] => {
    if (!epicSearchQuery.trim()) return epicItems;
    const query = epicSearchQuery.toLowerCase();
    return epicItems.filter((epic) => epic.name.toLowerCase().includes(query));
  }, [epicItems, epicSearchQuery]);

  // Group items by type (excluding sprints and epics - they're fetched dynamically)
  const groupedItems = useMemo(() => {
    return items.reduce(
      (acc, item) => {
        if (!acc[item.type]) {
          acc[item.type] = [];
        }
        acc[item.type].push(item);
        return acc;
      },
      {} as Record<MentionType, MentionItem[]>,
    );
  }, [items]);

  // Get available categories (those with items + sprint if boards with sprints available + epic always shown)
  const availableCategories = useMemo(() => {
    const categories = CATEGORY_ORDER.filter((type) => {
      if (type === 'sprint') {
        return boardsWithSprints.length > 0;
      }
      if (type === 'epic') {
        return true;
      }
      return groupedItems[type]?.length > 0;
    });
    return categories;
  }, [groupedItems, boardsWithSprints.length]);

  // Get items for current category
  const currentItems = useMemo(() => {
    if (
      selectedCategory &&
      selectedCategory !== 'sprint' &&
      selectedCategory !== 'epic' &&
      groupedItems[selectedCategory]
    ) {
      return groupedItems[selectedCategory];
    }
    return [];
  }, [selectedCategory, groupedItems]);

  // Filter items that match the search query (shown in category view)
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return [];
    const query = searchQuery.toLowerCase();

    // Filter regular items
    const matchingItems = items.filter(
      (item) => item.name.toLowerCase().includes(query) || item.parentName?.toLowerCase().includes(query),
    );

    // Also filter sprints if we have search query and sprints loaded
    const matchingSprints = sprintItems.filter((sprint) => sprint.name.toLowerCase().includes(query));

    // Also filter epics if we have search query and epics loaded
    const matchingEpics = epicItems.filter((epic) => epic.name.toLowerCase().includes(query));

    return [...matchingItems, ...matchingSprints, ...matchingEpics];
  }, [items, sprintItems, epicItems, searchQuery]);

  // Reset state when popover opens/closes
  useEffect(() => {
    if (isOpen) {
      setViewMode('categories');
      setSelectedCategory(null);
      setSelectedBoardId(null);
      setSelectedBoardName(null);
      setEpicSearchQuery('');
      setSelectedIndex(0);
    }
  }, [isOpen]);

  // Reset selection when switching views
  useEffect(() => {
    setSelectedIndex(0);
    if (viewMode !== 'epics') {
      setEpicSearchQuery('');
    }
  }, [viewMode]);

  // Auto-focus epic search input when entering epics view
  useEffect(() => {
    if (viewMode === 'epics' && !isLoadingEpics && epicSearchRef.current) {
      epicSearchRef.current.focus();
    }
  }, [viewMode, isLoadingEpics]);

  // Reset selected index when epic search query changes
  // biome-ignore lint/correctness/useExhaustiveDependencies: epicSearchQuery change triggers index reset
  useEffect(() => {
    setSelectedIndex(0);
  }, [epicSearchQuery]);

  // Calculate position based on anchor element
  useEffect(() => {
    if (isOpen && anchorRef?.current) {
      const rect = anchorRef.current.getBoundingClientRect();
      setPopoverPosition({
        top: rect.top - 8,
        left: rect.left + 16,
        width: 280,
      });
    }
  }, [isOpen, anchorRef]);

  const handleCategorySelect = useCallback((category: MentionType) => {
    setSelectedCategory(category);
    if (category === 'sprint') {
      setViewMode('boards');
    } else if (category === 'epic') {
      setViewMode('epics');
    } else {
      setViewMode('items');
    }
    setSelectedIndex(0);
  }, []);

  const handleBoardSelect = useCallback((board: BoardItem) => {
    setSelectedBoardId(board.id);
    setSelectedBoardName(board.name);
    setViewMode('sprints');
    setSelectedIndex(0);
  }, []);

  const handleBackToCategories = useCallback(() => {
    setViewMode('categories');
    setSelectedCategory(null);
    setSelectedBoardId(null);
    setSelectedBoardName(null);
    setEpicSearchQuery('');
    setSelectedIndex(0);
  }, []);

  const handleBackToBoards = useCallback(() => {
    setViewMode('boards');
    setSelectedBoardId(null);
    setSelectedBoardName(null);
    setSelectedIndex(0);
  }, []);

  // Get current list length based on view mode
  const getCurrentListLength = useCallback(() => {
    switch (viewMode) {
      case 'categories':
        return filteredItems.length + availableCategories.length;
      case 'items':
        return currentItems.length;
      case 'boards':
        return boardsWithSprints.length;
      case 'sprints':
        return sprintItems.length;
      case 'epics':
        return filteredEpicItems.length;
      default:
        return 0;
    }
  }, [
    viewMode,
    filteredItems.length,
    availableCategories.length,
    currentItems.length,
    boardsWithSprints.length,
    sprintItems.length,
    filteredEpicItems.length,
  ]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      // When epic search is focused, let the input handle most keys
      if (viewMode === 'epics' && epicSearchRef.current === document.activeElement) {
        // Only intercept navigation keys
        if (!['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(e.key)) {
          return;
        }
      }

      const maxIndex = getCurrentListLength() - 1;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex((prev) => (prev < maxIndex ? prev + 1 : prev));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case 'Enter':
        case 'Tab':
          e.preventDefault();
          if (viewMode === 'categories') {
            if (selectedIndex < filteredItems.length) {
              const item = filteredItems[selectedIndex];
              if (item) onSelect(item);
            } else {
              const categoryIndex = selectedIndex - filteredItems.length;
              const category = availableCategories[categoryIndex];
              if (category) handleCategorySelect(category);
            }
          } else if (viewMode === 'items') {
            const item = currentItems[selectedIndex];
            if (item) onSelect(item);
          } else if (viewMode === 'boards') {
            const board = boardsWithSprints[selectedIndex];
            if (board) handleBoardSelect(board);
          } else if (viewMode === 'sprints') {
            const sprint = sprintItems[selectedIndex];
            if (sprint) onSelect(sprint);
          } else if (viewMode === 'epics') {
            const epic = filteredEpicItems[selectedIndex];
            if (epic) onSelect(epic);
          }
          break;
        case 'Escape':
          e.preventDefault();
          if (viewMode === 'sprints') {
            handleBackToBoards();
          } else if (viewMode === 'boards' || viewMode === 'items' || viewMode === 'epics') {
            handleBackToCategories();
          } else {
            onClose();
          }
          break;
        case 'ArrowLeft':
        case 'Backspace':
          if (viewMode === 'sprints') {
            e.preventDefault();
            handleBackToBoards();
          } else if (viewMode === 'boards' || viewMode === 'items') {
            e.preventDefault();
            handleBackToCategories();
          }
          // Note: don't handle Backspace for epics view — let it work in the search input
          break;
        case 'ArrowRight':
          if (viewMode === 'categories' && selectedIndex >= filteredItems.length) {
            e.preventDefault();
            const categoryIndex = selectedIndex - filteredItems.length;
            const category = availableCategories[categoryIndex];
            if (category) handleCategorySelect(category);
          } else if (viewMode === 'boards') {
            e.preventDefault();
            const board = boardsWithSprints[selectedIndex];
            if (board) handleBoardSelect(board);
          }
          break;
      }
    },
    [
      isOpen,
      viewMode,
      getCurrentListLength,
      selectedIndex,
      filteredItems,
      availableCategories,
      currentItems,
      boardsWithSprints,
      sprintItems,
      filteredEpicItems,
      handleCategorySelect,
      handleBoardSelect,
      handleBackToCategories,
      handleBackToBoards,
      onSelect,
      onClose,
    ],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Scroll selected item into view
  useEffect(() => {
    if (listRef.current) {
      const selectedElement = listRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedElement) {
        selectedElement.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [selectedIndex]);

  if (!isOpen) {
    return null;
  }

  const renderHeader = () => {
    if (viewMode === 'epics') {
      return (
        <EpicHeaderContainer>
          <HeaderWithBack>
            <BackButton onClick={handleBackToCategories}>
              <Icon name="arrow_back" size={16} style={{ cursor: 'pointer' }} />
            </BackButton>
            <Text size="xs" fw={500} c={getMentionTypeColor('epic')}>
              {getMentionTypeLabelPlural('epic', i18n.t)}
            </Text>
          </HeaderWithBack>
          {!isLoadingEpics && epicItems.length > 0 && (
            <TextInput
              ref={epicSearchRef}
              size="xs"
              placeholder="Search epics..."
              value={epicSearchQuery}
              onChange={(e) => setEpicSearchQuery(e.currentTarget.value)}
              leftSection={<Icon name="search" size={14} />}
              styles={{
                input: {
                  background: 'var(--flora-popover-bg, white)',
                  borderColor: 'var(--flora-popover-border, var(--mantine-color-default-border))',
                },
              }}
            />
          )}
        </EpicHeaderContainer>
      );
    }
    if (viewMode === 'sprints' && selectedBoardName) {
      return (
        <HeaderWithBack>
          <BackButton onClick={handleBackToBoards}>
            <Icon name="arrow_back" size={16} style={{ cursor: 'pointer' }} />
          </BackButton>
          <Text size="xs" fw={500} c={getMentionTypeColor('sprint')}>
            {getMentionTypeLabel('sprint', i18n.t)} in {selectedBoardName}
          </Text>
        </HeaderWithBack>
      );
    }
    if (viewMode === 'boards') {
      return (
        <HeaderWithBack>
          <BackButton onClick={handleBackToCategories}>
            <Icon name="arrow_back" size={16} style={{ cursor: 'pointer' }} />
          </BackButton>
          <Text size="xs" fw={500} c={getMentionTypeColor('sprint')}>
            Select a Board
          </Text>
        </HeaderWithBack>
      );
    }
    if (viewMode === 'items' && selectedCategory) {
      return (
        <HeaderWithBack>
          <BackButton onClick={handleBackToCategories}>
            <Icon name="arrow_back" size={16} style={{ cursor: 'pointer' }} />
          </BackButton>
          <Text size="xs" fw={500} c={getMentionTypeColor(selectedCategory)}>
            {getMentionTypeLabelPlural(selectedCategory, i18n.t)}
          </Text>
        </HeaderWithBack>
      );
    }
    return (
      <Text size="xs" c="dimmed" fw={500}>
        {searchQuery ? `Results for "${searchQuery}"` : 'Select a category'}
      </Text>
    );
  };

  const renderContent = () => {
    // Epics view
    if (viewMode === 'epics') {
      if (isLoadingEpics) {
        return (
          <LoadingContainer>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading epics...
            </Text>
          </LoadingContainer>
        );
      }
      if (isErrorEpics) {
        return (
          <EmptyState>
            <WarningMessage>
              <Icon name="warning" color={newCOLORS.orange} size={20} />
              <Text size="sm" c={newCOLORS.orange}>
                Failed to load epics. Please try again.
              </Text>
            </WarningMessage>
          </EmptyState>
        );
      }
      if (epicItems.length === 0) {
        return (
          <EmptyState>
            <Text size="sm" c="dimmed">
              No epics found
            </Text>
          </EmptyState>
        );
      }
      if (filteredEpicItems.length === 0) {
        return (
          <EmptyState>
            <Text size="sm" c="dimmed">
              No epics matching &quot;{epicSearchQuery}&quot;
            </Text>
          </EmptyState>
        );
      }
      return filteredEpicItems.map((epic, index) => (
        <MentionItemButton
          key={`epic-${epic.id}`}
          data-index={index}
          $isSelected={index === selectedIndex}
          onClick={() => onSelect(epic)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <ItemIcon>
            <Icon name={CATEGORY_ICONS.epic} color={getMentionTypeColor('epic')} size={16} />
          </ItemIcon>
          <ItemContent>
            <Text size="sm" fw={500} lineClamp={1}>
              {epic.name}
            </Text>
          </ItemContent>
        </MentionItemButton>
      ));
    }

    // Sprints view
    if (viewMode === 'sprints') {
      if (isLoadingSprints) {
        return (
          <LoadingContainer>
            <Loader size="sm" />
            <Text size="sm" c="dimmed">
              Loading sprints...
            </Text>
          </LoadingContainer>
        );
      }
      if (sprintItems.length === 0) {
        return (
          <EmptyState>
            <Text size="sm" c="dimmed">
              {`No ${i18n.t('common.work_periods').toLowerCase()} found for this ${i18n.t('common.board').toLowerCase()}`}
            </Text>
          </EmptyState>
        );
      }
      return sprintItems.map((sprint, index) => (
        <MentionItemButton
          key={`sprint-${sprint.id}`}
          data-index={index}
          $isSelected={index === selectedIndex}
          onClick={() => onSelect(sprint)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <ItemIcon>
            <Icon name={CATEGORY_ICONS.sprint} color={getMentionTypeColor('sprint')} size={16} />
          </ItemIcon>
          <ItemContent>
            <Text size="sm" fw={500} lineClamp={1}>
              {sprint.name}
            </Text>
          </ItemContent>
        </MentionItemButton>
      ));
    }

    // Boards view (for sprint selection)
    if (viewMode === 'boards') {
      return boardsWithSprints.map((board, index) => (
        <MentionItemButton
          key={`board-select-${board.id}`}
          data-index={index}
          $isSelected={index === selectedIndex}
          onClick={() => handleBoardSelect(board)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <ItemIcon>
            <Icon name={CATEGORY_ICONS.board} color={getMentionTypeColor('board')} size={16} />
          </ItemIcon>
          <ItemContent>
            <Text size="sm" fw={500} lineClamp={1}>
              {board.name}
            </Text>
            <Text size="xs" c="dimmed" lineClamp={1}>
              {board.projectName}
            </Text>
          </ItemContent>
          <Icon name="chevron_right" color={newCOLORS.gray} size={16} style={{ cursor: 'pointer' }} />
        </MentionItemButton>
      ));
    }

    // Items view (for regular categories)
    if (viewMode === 'items') {
      return currentItems.map((item, index) => (
        <MentionItemButton
          key={`${item.type}-${item.id}`}
          data-index={index}
          $isSelected={index === selectedIndex}
          onClick={() => onSelect(item)}
          onMouseEnter={() => setSelectedIndex(index)}
        >
          <ItemIcon>
            <Icon name={CATEGORY_ICONS[item.type]} color={getMentionTypeColor(item.type)} size={16} />
          </ItemIcon>
          <ItemContent>
            <Text size="sm" fw={500} lineClamp={1}>
              {item.name}
            </Text>
            {item.parentName && (
              <Text size="xs" c="dimmed" lineClamp={1}>
                in {item.parentName}
              </Text>
            )}
          </ItemContent>
        </MentionItemButton>
      ));
    }

    // Categories view (default)
    return (
      <>
        {/* Filtered items matching search query */}
        {filteredItems.length > 0 && (
          <FilteredSection>
            <SectionLabel>
              <Text size="xs" c="dimmed" fw={500}>
                Matches
              </Text>
            </SectionLabel>
            {filteredItems.map((item, index) => (
              <MentionItemButton
                key={`filtered-${item.type}-${item.id}`}
                data-index={index}
                $isSelected={index === selectedIndex}
                onClick={() => onSelect(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                <ItemIcon>
                  <Icon name={CATEGORY_ICONS[item.type]} color={getMentionTypeColor(item.type)} size={16} />
                </ItemIcon>
                <ItemContent>
                  <Text size="sm" fw={500} lineClamp={1}>
                    {item.name}
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {getMentionTypeLabel(item.type, i18n.t)}
                    {item.parentName && ` · ${item.parentName}`}
                  </Text>
                </ItemContent>
              </MentionItemButton>
            ))}
          </FilteredSection>
        )}
        {/* Category buttons */}
        {filteredItems.length > 0 && availableCategories.length > 0 && (
          <SectionLabel>
            <Text size="xs" c="dimmed" fw={500}>
              Browse by type
            </Text>
          </SectionLabel>
        )}
        {availableCategories.map((type, index) => {
          const actualIndex = filteredItems.length + index;
          const count = type === 'sprint' ? boardsWithSprints.length : groupedItems[type]?.length || 0;
          const subtitle =
            type === 'sprint' ? `${count} boards` : type === 'epic' ? 'Fetched on select' : `${count} available`;
          return (
            <CategoryButton
              key={type}
              data-index={actualIndex}
              $isSelected={actualIndex === selectedIndex}
              onClick={() => handleCategorySelect(type)}
              onMouseEnter={() => setSelectedIndex(actualIndex)}
            >
              <CategoryIcon>
                <Icon name={CATEGORY_ICONS[type]} color={getMentionTypeColor(type)} size={20} />
              </CategoryIcon>
              <CategoryContent>
                <Text size="sm" fw={500}>
                  {getMentionTypeLabelPlural(type, i18n.t)}
                </Text>
                <Text size="xs" c="dimmed">
                  {subtitle}
                </Text>
              </CategoryContent>
              <Icon name="chevron_right" color={newCOLORS.gray} size={18} style={{ cursor: 'pointer' }} />
            </CategoryButton>
          );
        })}
      </>
    );
  };

  return (
    <Portal>
      <PopoverContainer
        data-theme={dataTheme}
        style={{
          top: popoverPosition.top,
          left: popoverPosition.left,
          width: popoverPosition.width,
        }}
      >
        <Paper
          shadow="md"
          radius="md"
          withBorder
          style={{
            background: 'var(--flora-popover-bg, white)',
            borderColor: 'var(--flora-popover-border, var(--mantine-color-default-border))',
          }}
        >
          <PopoverHeader>{renderHeader()}</PopoverHeader>
          <ScrollArea.Autosize mah={250} ref={listRef} scrollbars="y">
            <PopoverContent>{renderContent()}</PopoverContent>
          </ScrollArea.Autosize>
        </Paper>
      </PopoverContainer>
    </Portal>
  );
};

const PopoverContainer = styled.div`
  position: fixed;
  transform: translateY(-100%);
  z-index: 9999;
  max-height: 300px;
  min-width: 200px;
  max-width: 320px;
`;

const PopoverHeader = styled.div`
  padding: 8px 12px;
  border-bottom: 1px solid var(--flora-popover-border, ${newCOLORS.lightGray});
`;

const HeaderWithBack = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EpicHeaderContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  padding: 4px;
  border-radius: 4px;
  cursor: pointer;
  color: var(--flora-popover-back-btn, ${newCOLORS.darkGray});

  &:hover {
    background: var(--flora-popover-hover, ${newCOLORS.lighterGray});
  }
`;

const PopoverContent = styled.div`
  padding: 8px;
`;

const CategoryButton = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 8px;
  border-radius: 8px;
  background: ${(props) => (props.$isSelected ? `var(--flora-popover-hover, ${newCOLORS.lighterGray})` : 'transparent')};
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: var(--flora-popover-hover, ${newCOLORS.lighterGray});
  }
`;

const CategoryIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: var(--flora-popover-icon-bg, ${newCOLORS.lightGray}30);
  flex-shrink: 0;
`;

const CategoryContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const MentionItemButton = styled.div<{ $isSelected: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px;
  border-radius: 6px;
  background: ${(props) => (props.$isSelected ? `var(--flora-popover-hover, ${newCOLORS.lighterGray})` : 'transparent')};
  transition: background 0.15s ease;
  cursor: pointer;

  &:hover {
    background: var(--flora-popover-hover, ${newCOLORS.lighterGray});
  }
`;

const ItemIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ItemContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const FilteredSection = styled.div`
  margin-bottom: 8px;
`;

const SectionLabel = styled.div`
  padding: 4px 8px 4px 8px;
  margin-top: 4px;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
`;

const WarningMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  text-align: center;
`;
